import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  ArrowLeft, BarChart3, TrendingUp, Users, Download, Calendar,
  FileText, RefreshCw, ChevronDown, User
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, 
  Tooltip, Legend, ResponsiveContainer, ComposedChart, Area 
} from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Reports() {
  const { getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [classes, setClasses] = useState([]);
  const [students, setStudents] = useState([]);
  const [selectedClass, setSelectedClass] = useState('all');
  const [selectedStudent, setSelectedStudent] = useState('');
  const [selectedMonth, setSelectedMonth] = useState('current');
  
  // Data states
  const [classComparison, setClassComparison] = useState([]);
  const [monthlyTrend, setMonthlyTrend] = useState([]);
  const [studentProgress, setStudentProgress] = useState(null);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    if (selectedClass && selectedClass !== 'all') {
      fetchClassStudents(selectedClass);
    }
  }, [selectedClass]);

  const fetchInitialData = async () => {
    try {
      const [classesRes, comparisonRes, trendRes] = await Promise.all([
        axios.get(`${API}/admin/classes`, { headers: getAuthHeaders() }),
        axios.get(`${API}/admin/reports/class-comparison`, { headers: getAuthHeaders() }),
        axios.get(`${API}/admin/reports/monthly-trend`, { headers: getAuthHeaders() }),
      ]);
      setClasses(classesRes.data);
      setClassComparison(comparisonRes.data);
      setMonthlyTrend(trendRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Деректерді жүктеу кезінде қате');
    } finally {
      setLoading(false);
    }
  };

  const fetchClassStudents = async (classId) => {
    try {
      const response = await axios.get(`${API}/admin/classes/${classId}/students`, {
        headers: getAuthHeaders()
      });
      setStudents(response.data);
    } catch (error) {
      console.error('Error fetching students:', error);
    }
  };

  const fetchStudentProgress = async (studentId) => {
    if (!studentId) return;
    try {
      const response = await axios.get(`${API}/admin/reports/student-progress/${studentId}`, {
        headers: getAuthHeaders()
      });
      setStudentProgress(response.data);
    } catch (error) {
      console.error('Error fetching student progress:', error);
      toast.error('Оқушы деректерін жүктеу кезінде қате');
    }
  };

  const downloadComparisonPdf = async () => {
    setDownloadingPdf(true);
    try {
      const response = await axios.get(`${API}/admin/reports/comparison-pdf`, {
        headers: getAuthHeaders(),
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `siniflar_salystyrma_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('PDF жүктелді');
    } catch (error) {
      toast.error('PDF жүктеу кезінде қате');
    } finally {
      setDownloadingPdf(false);
    }
  };

  const downloadStudentProgressPdf = async () => {
    if (!selectedStudent) return;
    setDownloadingPdf(true);
    try {
      const response = await axios.get(`${API}/admin/reports/student-progress-pdf/${selectedStudent}`, {
        headers: getAuthHeaders(),
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `oqushy_damuy_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('PDF жүктелді');
    } catch (error) {
      toast.error('PDF жүктеу кезінде қате');
    } finally {
      setDownloadingPdf(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-primary text-white py-4 shadow-lg">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => navigate('/admin')}
                className="text-white hover:bg-white/20"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Артқа
              </Button>
              <div className="flex items-center space-x-2">
                <BarChart3 className="h-6 w-6" />
                <h1 className="text-xl font-bold">Есептер & Талдау</h1>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-6">
        <Tabs defaultValue="comparison" className="space-y-6">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="comparison">
              <BarChart3 className="w-4 h-4 mr-2" />
              Сыныптар салыстыру
            </TabsTrigger>
            <TabsTrigger value="trend">
              <TrendingUp className="w-4 h-4 mr-2" />
              Айлық үрдіс
            </TabsTrigger>
            <TabsTrigger value="student">
              <User className="w-4 h-4 mr-2" />
              Оқушы дамуы
            </TabsTrigger>
          </TabsList>

          {/* Class Comparison Tab */}
          <TabsContent value="comparison" className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold">Сыныптар арасындағы салыстыру</h2>
                <p className="text-muted text-sm">Барлық сыныптардың орташа ұпайлары</p>
              </div>
              <Button onClick={downloadComparisonPdf} disabled={downloadingPdf}>
                <Download className="w-4 h-4 mr-2" />
                {downloadingPdf ? 'Жүктелуде...' : 'PDF жүктеу'}
              </Button>
            </div>

            <Card>
              <CardHeader>
                <CardTitle>Сыныптар бойынша орташа ұпай</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[400px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={classComparison} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" />
                      <YAxis dataKey="class_name" type="category" width={60} />
                      <Tooltip 
                        formatter={(value, name) => [
                          `${value} ұпай`, 
                          name === 'average_score' ? 'Орташа' : name
                        ]}
                      />
                      <Legend />
                      <Bar 
                        dataKey="average_score" 
                        name="Орташа ұпай"
                        fill="#4f46e5"
                        radius={[0, 4, 4, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-green-600">Мақтау саны бойынша</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-[300px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={classComparison}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="class_name" />
                        <YAxis />
                        <Tooltip />
                        <Bar dataKey="merit_count" name="Мақтау" fill="#22c55e" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-red-600">Сөгіс саны бойынша</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-[300px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={classComparison}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="class_name" />
                        <YAxis />
                        <Tooltip />
                        <Bar dataKey="demerit_count" name="Сөгіс" fill="#ef4444" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Monthly Trend Tab */}
          <TabsContent value="trend" className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold">Айлық үрдіс</h2>
              <p className="text-muted text-sm">Соңғы 6 айдағы жалпы көрсеткіштер</p>
            </div>

            <Card>
              <CardHeader>
                <CardTitle>Мақтау vs Сөгіс үрдісі</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[400px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={monthlyTrend}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis yAxisId="left" />
                      <YAxis yAxisId="right" orientation="right" />
                      <Tooltip />
                      <Legend />
                      <Area 
                        yAxisId="left"
                        type="monotone" 
                        dataKey="total_merits" 
                        name="Мақтау"
                        fill="#22c55e" 
                        fillOpacity={0.3}
                        stroke="#22c55e" 
                      />
                      <Area 
                        yAxisId="left"
                        type="monotone" 
                        dataKey="total_demerits" 
                        name="Сөгіс"
                        fill="#ef4444" 
                        fillOpacity={0.3}
                        stroke="#ef4444" 
                      />
                      <Line 
                        yAxisId="right"
                        type="monotone" 
                        dataKey="average_score" 
                        name="Орташа ұпай"
                        stroke="#4f46e5" 
                        strokeWidth={3}
                        dot={{ fill: '#4f46e5' }}
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {monthlyTrend.slice(-3).map((month, index) => (
                <Card key={index}>
                  <CardContent className="pt-6">
                    <div className="text-center">
                      <p className="text-sm text-muted mb-2">{month.month}</p>
                      <p className="text-3xl font-bold text-primary">{month.average_score}</p>
                      <p className="text-xs text-muted">Орташа ұпай</p>
                      <div className="flex justify-center gap-4 mt-3">
                        <span className="text-sm text-green-600">+{month.total_merits} М</span>
                        <span className="text-sm text-red-600">-{month.total_demerits} С</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Student Progress Tab */}
          <TabsContent value="student" className="space-y-6">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <h2 className="text-2xl font-bold">Оқушы дамуы</h2>
                <p className="text-muted text-sm">Жеке оқушының уақыт бойынша ілгерілеуі</p>
              </div>
              
              <div className="flex items-center gap-4">
                <Select value={selectedClass} onValueChange={(v) => { setSelectedClass(v); setSelectedStudent(''); setStudentProgress(null); }}>
                  <SelectTrigger className="w-[150px]">
                    <SelectValue placeholder="Сынып" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Барлығы</SelectItem>
                    {classes.map(cls => (
                      <SelectItem key={cls.class_id} value={cls.class_id}>
                        {cls.class_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {selectedClass !== 'all' && (
                  <Select value={selectedStudent} onValueChange={(v) => { setSelectedStudent(v); fetchStudentProgress(v); }}>
                    <SelectTrigger className="w-[200px]">
                      <SelectValue placeholder="Оқушы таңдаңыз" />
                    </SelectTrigger>
                    <SelectContent>
                      {students.map(student => (
                        <SelectItem key={student.student_id || student.id} value={student.student_id || student.id}>
                          {student.student_name || student.full_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}

                {studentProgress && (
                  <Button onClick={downloadStudentProgressPdf} disabled={downloadingPdf}>
                    <Download className="w-4 h-4 mr-2" />
                    PDF
                  </Button>
                )}
              </div>
            </div>

            {!studentProgress ? (
              <Card>
                <CardContent className="py-12 text-center text-muted">
                  <User className="w-16 h-16 mx-auto mb-4 opacity-30" />
                  <p>Оқушыны таңдаңыз</p>
                </CardContent>
              </Card>
            ) : (
              <>
                {/* Student Info */}
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-xl font-bold">{studentProgress.student_name}</h3>
                        <p className="text-muted">{studentProgress.class_name}</p>
                      </div>
                      <div className="text-right">
                        <p className={`text-3xl font-bold ${studentProgress.current_score >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {studentProgress.current_score >= 0 ? '+' : ''}{studentProgress.current_score}
                        </p>
                        <p className="text-sm text-muted">Ағымдағы ұпай</p>
                      </div>
                    </div>
                    
                    {studentProgress.change_percent !== null && (
                      <div className={`mt-4 p-3 rounded-lg ${studentProgress.change_percent >= 0 ? 'bg-green-50' : 'bg-red-50'}`}>
                        <p className={`font-medium ${studentProgress.change_percent >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                          {studentProgress.change_percent >= 0 ? '📈' : '📉'} Өткен айға қарағанда {Math.abs(studentProgress.change_percent)}% {studentProgress.change_percent >= 0 ? 'жақсарды' : 'төмендеді'}
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Progress Chart */}
                <Card>
                  <CardHeader>
                    <CardTitle>Айлық ілгерілеу</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-[350px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <ComposedChart data={studentProgress.monthly_data}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="month" />
                          <YAxis />
                          <Tooltip />
                          <Legend />
                          <Bar dataKey="merits" name="Мақтау" fill="#22c55e" radius={[4, 4, 0, 0]} />
                          <Bar dataKey="demerits" name="Сөгіс" fill="#ef4444" radius={[4, 4, 0, 0]} />
                          <Line 
                            type="monotone" 
                            dataKey="score" 
                            name="Жалпы ұпай"
                            stroke="#4f46e5" 
                            strokeWidth={3}
                            dot={{ fill: '#4f46e5', r: 5 }}
                          />
                        </ComposedChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>

                {/* Stats Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Card>
                    <CardContent className="pt-6 text-center">
                      <p className="text-3xl font-bold text-green-600">{studentProgress.total_merits}</p>
                      <p className="text-sm text-muted">Жалпы мақтау</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-6 text-center">
                      <p className="text-3xl font-bold text-red-600">{studentProgress.total_demerits}</p>
                      <p className="text-sm text-muted">Жалпы сөгіс</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-6 text-center">
                      <p className="text-3xl font-bold text-primary">{studentProgress.best_month || '-'}</p>
                      <p className="text-sm text-muted">Ең жақсы ай</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-6 text-center">
                      <p className="text-3xl font-bold text-amber-600">{studentProgress.rank || '-'}</p>
                      <p className="text-sm text-muted">Сыныптағы орны</p>
                    </CardContent>
                  </Card>
                </div>
              </>
            )}
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
